// Guarda das rotas de dispositivo (5.4): exige sessão válida e o modo do link correto.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { LinkIcon, ShieldOff } from "lucide-react";
import { accessLinkModeLabels, type AccessLinkMode, type DeviceSession } from "@centro-cirurgico/contracts";
import { Brand, Notice } from "@centro-cirurgico/ui";

import { useDeviceSession } from "../hooks/useDeviceSession";
import { LoadingState } from "./LoadingState";
import type { SessionInvalidReason } from "../utils/session";

export interface RequireDeviceSessionProps {
  mode: AccessLinkMode;
  children: (session: DeviceSession) => ReactNode;
}

export function RequireDeviceSession({ mode, children }: RequireDeviceSessionProps) {
  const { session, reason, renewing } = useDeviceSession();

  if (!session && renewing) {
    return (
      <div className="app">
        <LoadingState message="Renovando acesso do dispositivo…" />
      </div>
    );
  }
  if (!session) return <NoSessionScreen reason={reason} />;

  if (session.mode !== mode) {
    const target = session.mode === "reception" ? "/recepcao" : "/sala";
    return (
      <DeviceMessagePage icon={<LinkIcon aria-hidden="true" size={40} />} title="Este link é de outro modo">
        <p>
          O link aberto neste dispositivo é do modo <strong>{accessLinkModeLabels[session.mode]}</strong> (
          {session.label}). Esta tela exige o modo <strong>{accessLinkModeLabels[mode]}</strong>.
        </p>
        <Link className="primary big-button" to={target}>
          Abrir {accessLinkModeLabels[session.mode]}
        </Link>
      </DeviceMessagePage>
    );
  }

  return <>{children(session)}</>;
}

export function NoSessionScreen({ reason }: { reason: SessionInvalidReason | null }) {
  const revoked = reason === "revoked" || reason === "expired";
  return (
    <DeviceMessagePage
      icon={<ShieldOff aria-hidden="true" size={40} />}
      title={revoked ? "Link revogado ou expirado" : "Sem sessão de dispositivo"}
    >
      {revoked ? (
        <Notice error="Link revogado ou expirado. Peça um novo link à coordenação." />
      ) : (
        <p>
          Este dispositivo ainda não abriu um link de acesso. Peça à coordenação o link de Recepção ou de Sala e
          abra-o neste navegador.
        </p>
      )}
      <p className="subtle">
        Os links são gerados no painel administrativo em <strong>Links de acesso</strong> e podem ser abertos pelo QR
        Code.
      </p>
    </DeviceMessagePage>
  );
}

export function DeviceMessagePage({
  icon,
  title,
  children
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="login-page">
      <header className="login-header">
        <Brand tag="DISPOSITIVO" variant="full" />
      </header>
      <main className="device-message">
        <section className="login-card device-message-card" aria-labelledby="device-message-title">
          <span className="square-icon">{icon}</span>
          <h2 id="device-message-title">{title}</h2>
          {children}
        </section>
      </main>
    </div>
  );
}
